'use client';

import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@eliadmin/core/src/components/ui/accordion';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTrigger,
} from '@eliadmin/core/src/components/ui/sheet';
import { HEADER_CONFIG } from '@eliadmin/core/src/constants/header-config';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { getSafeHref, isRelativeHref } from '@eliadmin/core/src/utils/url-utils';
import Link from '@eliadmin/core/src/components/link';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { LuSend } from 'react-icons/lu';
import { MdOutlinePhoneInTalk } from 'react-icons/md';
import { FaRegUserCircle } from 'react-icons/fa';
import { useCurrentStore } from '../../utils/use-store-utils';

const CategoryItem = ({ category, layer }: { category: CategoriesEntity; layer?: number }) => {
  const hasChildren = !!category.childs?.length;

  return (
    <AccordionItem
      className={cn('flex w-full flex-col border-none', layer && 'ml-2 font-semibold')}
      value={category.slug + layer?.toString()}
    >
      <li
        className={cn(
          'flex w-full flex-1 flex-row items-center justify-between overflow-hidden whitespace-normal last:border-r-0',
          'ea-menu-sidebar__list-item',
          // className?.listItem,
        )}
      >
        <Link prefetchOnHover href={category.slug ?? '#'} className="w-full" prefetch={false}>
          <SheetClose
            className={cn(
              'text-left font-semibold',
              layer === 1 && 'text-sm font-medium',
              layer && layer > 1 && 'text-xs font-light',
            )}
          >
            {category.name}
          </SheetClose>
        </Link>
        {hasChildren && (
          <AccordionTrigger className="h-5 w-5" iconstyles="w-5 h-5 text-primary flex-1" />
        )}
      </li>
      <AccordionContent className="pb-0 ">
        <Accordion
          type="single"
          collapsible
          className="flex flex-col items-start justify-start gap-2"
        >
          {hasChildren &&
            category.childs?.map((child, index) => (
              <CategoryItem key={index} category={child} layer={layer ? layer + 1 : 1} />
            ))}
        </Accordion>
      </AccordionContent>
    </AccordionItem>
  );
};

export type MobileSidebarProps = {
  className?: {
    trigger?: string;
    content?: string;
    firstSeparator?: string;
    menuList?: string;
    menuLink?: string;
    menuGroup?: string;
    menuSeparator?: string;
    buttonWrapper?: string;
    button?: string;
    iconsWrapper?: string;
    icon?: string;
  };
  side?: 'right' | 'left' | 'bottom' | 'top';
  firstSeparator?: boolean;
  menu?: {
    type: 'page';
    name: string;
    path?: string;
  }[];
  menuArrow?: boolean;
  menuSeparator?: boolean;
  categories?: CategoriesEntity[];
};

const MobileSidebar = (props: MobileSidebarProps) => {
  const generalLayout = useGlobalStore(state => state.generalLayout);
  const { t } = useTranslations();

  const categories = props.categories;

  const me = useCustomerStore(s => s.me);

  const { isEshop } = useCurrentStore();

  return (
    <>
      <Sheet>
        <SheetTrigger
          className={cn(
            'flex items-center justify-start gap-2 text-xs text-white',
            'ea-mobile-sidebar__trigger',
            props.className?.trigger,
          )}
        >
          {HEADER_CONFIG?.sidebar?.triggerIcon}
          MENU
        </SheetTrigger>
        <SheetContent
          side={props.side}
          className={cn(
            'flex w-full max-w-[330px] flex-col items-start justify-start gap-4 overflow-auto px-6 pb-32 pt-24',
            'ea-mobile-sidebar__content',
            props.className?.content,
          )}
          closeIcon={{
            wrapper: 'right-6 top-8',
            icon: 'text-black/30 h-6 w-6 font-bold',
          }}
        >
          {isEshop && (
            <>
              <Accordion
                type="single"
                collapsible
                className="flex w-full flex-col items-start justify-start gap-4"
              >
                {categories?.map((category, index) => (
                  <CategoryItem key={index} category={category} />
                ))}
              </Accordion>
              <div className="h-[1px] w-full bg-border" />
            </>
          )}

          {props.menu?.map((item, index) => {
            const Comp = item.path ? (isRelativeHref(getSafeHref(item.path)) ? Link : 'a') : 'span';
            return (
              <SheetClose key={index} asChild>
                <Comp
                  href={item.path ? getSafeHref(item.path) : (undefined as any)}
                  className="flex w-full"
                  prefetch={false}
                >
                  <p className={cn('text-sm', '')}>{item.name}</p>
                </Comp>
              </SheetClose>
            );
          })}
          {isEshop && (
            <>
              {me ? (
                <SheetClose
                  asChild
                  className="flex min-h-12 w-full items-center justify-center gap-2 border border-border text-sm font-medium"
                >
                  <Link prefetchOnHover prefetch={false} href={ROUTES.PROFILE()}>
                    <FaRegUserCircle size={20} /> {t('text1134')}
                  </Link>
                </SheetClose>
              ) : (
                <SheetClose
                  asChild
                  className="flex min-h-12 w-full items-center justify-center bg-primary text-sm font-medium tracking-tighter text-black"
                >
                  <Link prefetchOnHover prefetch={false} href={ROUTES.SIGN_IN()}>
                    {t('text189')}
                  </Link>
                </SheetClose>
              )}
            </>
          )}

          <div
            className={cn(
              'flex flex-col items-start justify-start gap-4 border-t border-t-border pt-4',
              isEshop && 'mt-14',
            )}
          >
            <p className="text-sm font-semibold">
              {t('text611')} {t('text612')}
            </p>
            <Link
              prefetchOnHover
              prefetch={false}
              href={`mailto:${generalLayout.contact?.email}`}
              className="flex items-center justify-start gap-3"
            >
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary">
                <LuSend size={16} className={cn(isEshop ? 'text-black' : 'text-white')} />
              </div>
              <p className="text-sm font-medium text-black">{generalLayout.contact?.email}</p>
            </Link>
            <Link
              prefetchOnHover
              prefetch={false}
              href={`tel:${generalLayout.contact?.phone}`}
              className="flex items-center justify-start gap-3"
            >
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary">
                <MdOutlinePhoneInTalk
                  size={16}
                  className={cn(isEshop ? 'text-black' : 'text-white')}
                />
              </div>
              <p className="text-sm font-medium text-black">{generalLayout.contact?.phone}</p>
            </Link>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default MobileSidebar;
